// src/pages/TradingChatSessionPage.jsx
import React, { useState, useRef, useEffect } from 'react';
import { getAuth } from 'firebase/auth';
import { getFirestore, collection, getDocs, query, where, orderBy, serverTimestamp } from 'firebase/firestore';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import Header from '../components/HeaderPostLogin.jsx';
import '../styles/homepage.css';
import { trackEvent } from '../utils/analytics/trackEvent.js';
import AhaLoadingOverlay from '../components/AhaLoadingOverlay.jsx';
import ContinueEmailCaptureOverlay from '../components/ContinueEmailCaptureOverlay.jsx';
import AnswerBox from '../components/ai/ahamoment/AnswerBox.jsx';
import TipBox from '../components/ai/ahamoment/TipBox.jsx';
import ActionsBox from '../components/ai/ahamoment/ActionsBox.jsx';
import SuggestedTopicsBox from '../components/ai/ahamoment/SuggestedTopicsBox.jsx';
import { parseAIAIResponse } from '../utils/parseAIAIResponse.js';

const TradingChatSessionPage = () => {
  const [messages, setMessages] = useState([]);
  const [suggestedTopics, setSuggestedTopics] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [tokenBalance, setTokenBalance] = useState(100);
  const [initialLoading, setInitialLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);

  const MAX_MESSAGES = 4;
  const { chatsessionid } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const auth = getAuth();
  const db = getFirestore();
  const chatEndRef = useRef(null);

  const [chatSessionId, setChatSessionId] = useState('');
  const initialQuestion = location.state?.question || 'How to spot liquidity grabs on charts?';

  useEffect(() => {
    const sessionFromURL = chatsessionid;
    if (sessionFromURL) {
      sessionStorage.setItem('fintorro_trading_chatsessionid', sessionFromURL);
      setChatSessionId(sessionFromURL);
    } else {
      let stored = sessionStorage.getItem('fintorro_trading_chatsessionid');
      if (!stored) {
        stored = crypto.randomUUID();
        sessionStorage.setItem('fintorro_trading_chatsessionid', stored);
      }
      setChatSessionId(stored);
    }
  }, [chatsessionid]);

  useEffect(() => {
    const unsub = auth.onAuthStateChanged(user => setCurrentUser(user));
    return () => unsub();
  }, []);

  // Fetch existing Firestore session or AI
  useEffect(() => {
    if (!chatSessionId) return;

    const fetchChat = async () => {
      try {
        const qSnap = await getDocs(
          query(
            collection(db, 'user_learn_aha_moment_ai_interactions'),
            where('chatsessionid', '==', chatSessionId),
            orderBy('messageid')
          )
        );

        if (!qSnap.empty) {
          const restored = [];
          qSnap.forEach(doc => {
            const d = doc.data();
            restored.push({ sender: 'user', text: d.usermessage });
            restored.push({ sender: 'ai', text: d.airesponse });
          });
          setMessages(restored);
          setInitialLoading(false);
          return;
        }

        // fallback: start fresh
        const res = await fetch('https://mcp-server-fintorro.onrender.com/ahaMomentAiChat', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            userid: currentUser?.uid || 'anonymous',
            chatsessionid: chatSessionId,
            usermessage: initialQuestion,
            messageCount: 0,
          }),
        });
        const data = await res.json();
        const aiResponse = data.airesponse || '⚠️ No response generated.';
        setMessages([
          { sender: 'user', text: initialQuestion },
          { sender: 'ai', text: aiResponse },
        ]);
        setSuggestedTopics(extractSuggestions(aiResponse));
        setInitialLoading(false);
      } catch (err) {
        console.error('❌ Fetch failed:', err);
        setInitialLoading(false);
      }
    };

    fetchChat();
  }, [chatSessionId, currentUser]);

  const extractSuggestions = (text) => {
    const lines = text.split('\n');
    const out = [];
    lines.forEach(line => {
      const match = line.match(/"([^"]+)"/);
      if (match) out.push(match[1]);
      else if (line.trim().startsWith('- ') || line.trim().startsWith('•'))
        out.push(line.trim().replace(/^[-•]\s*/, ''));
    });
    return out;
  };

  const handleSend = async () => {
    if (!input.trim() || loading) return;
    const msg = input.trim();
    setInput('');
    setMessages(prev => [...prev, { sender: 'user', text: msg }]);
    setLoading(true);

    try {
      const res = await fetch('https://mcp-server-fintorro.onrender.com/ahaMomentAiChat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userid: currentUser?.uid || 'anonymous',
          chatsessionid: chatSessionId,
          usermessage: msg,
          messageCount: messages.filter(m => m.sender === 'user').length,
        }),
      });
      const data = await res.json();
      const aiResponse = data.airesponse || '⚠️ No response.';
      setMessages(prev => [...prev, { sender: 'ai', text: aiResponse }]);
      setSuggestedTopics(extractSuggestions(aiResponse));

      if (!currentUser) setTokenBalance(prev => Math.max(prev - 30, 0));
      if (!currentUser && messages.filter(m => m.sender === 'user').length >= MAX_MESSAGES)
        setShowAuthModal(true);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
      chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <>
      <Header />
      {initialLoading && <AhaLoadingOverlay />}

      <main style={{ backgroundColor: '#232830', color: '#fff', minHeight: '100vh', paddingTop: '80px', display: 'flex' }}>
        {/* Sidebar */}
        <aside style={{ width: '260px', background: '#181B1F', padding: '1rem', overflowY: 'auto' }}>
          <h3 style={{ color: '#FF7C24', fontSize: '18px' }}>📘 Learning History</h3>
          {messages.filter(m => m.sender === 'user').map((m, i) => (
            <div key={i} style={{ color: '#fff', fontSize: '14px', marginTop: '1rem' }}>{m.text}</div>
          ))}
        </aside>

        {/* Chat area */}
        <section style={{ flex: 1, padding: '1rem', overflowY: 'auto' }}>
          {messages.map((m, i) => {
            const isAI = m.sender === 'ai';
            const parsed = isAI ? parseAIAIResponse(m.text) : null;
            return (
              <div key={i} style={{ marginBottom: '1.5rem' }}>
                {!isAI && (
                  <div style={{ backgroundColor: '#007bff', padding: '1rem', borderRadius: '16px', maxWidth: '90%', marginLeft: 'auto' }}>
                    {m.text}
                  </div>
                )}
                {isAI && (
                  <>
                    {parsed ? (
                      <>
                        {parsed.answer && <AnswerBox>{parsed.answer}</AnswerBox>}
                        {parsed.tip && <TipBox>{parsed.tip}</TipBox>}
                        {parsed.actions.length > 0 && <ActionsBox actions={parsed.actions} />}
                        {parsed.topics.length > 0 && <SuggestedTopicsBox topics={parsed.topics} />}
                      </>
                    ) : (
                      <AnswerBox>{m.text}</AnswerBox>
                    )}
                  </>
                )}
              </div>
            );
          })}

          {/* Suggested topics */}
          {suggestedTopics.length > 0 && (
            <div style={{ marginTop: '1rem', backgroundColor: '#2D2D2D', padding: '1rem', borderRadius: '12px' }}>
              <p style={{ textAlign: 'center', color: '#fff', fontWeight: 600 }}>📌 Suggested Next Topics to Learn</p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', justifyContent: 'center' }}>
                {suggestedTopics.map((t, i) => (
                  <button key={i} onClick={() => { setInput(t); handleSend(); }}
                    style={{
                      padding: '0.6rem 1rem', borderRadius: '10px',
                      backgroundColor: '#fff', color: '#232830', border: 'none',
                      cursor: 'pointer', fontWeight: '600'
                    }}>{t}</button>
                ))}
              </div>
            </div>
          )}

          {/* Token balance */}
          {!currentUser && (
            <div style={{
              marginTop: '1rem', backgroundColor: '#fff', color: '#FF7C24',
              padding: '8px 14px', borderRadius: '12px', fontWeight: '700',
              display: 'flex', justifyContent: 'center', gap: '0.5rem'
            }}>
              💰 <span style={{ fontWeight: '800' }}>{tokenBalance}</span> Tokens left
            </div>
          )}

          {/* Input */}
          <textarea rows={3} value={input} onChange={(e) => setInput(e.target.value)}
            placeholder="Ask about trading..." style={{
              width: '100%', borderRadius: '12px', border: '1px solid #ccc',
              padding: '1rem', marginTop: '1rem', color: '#232830'
            }} />
          <button onClick={handleSend} disabled={loading || !input.trim()}
            style={{
              backgroundColor: '#FF7C24', color: '#fff', border: 'none',
              padding: '0.75rem 1.5rem', borderRadius: '12px', marginTop: '0.5rem',
              fontWeight: '700', cursor: 'pointer'
            }}>
            {loading ? 'Thinking...' : 'Ask AI'}
          </button>

          <p style={{ textAlign: 'center', fontSize: '13px', marginTop: '1rem' }}>
            🤖 AI can make mistakes. Please use with discretion.
          </p>
        </section>
      </main>

      {showAuthModal && (
        <ContinueEmailCaptureOverlay
          heading="🚀 Unlock More Trading Insights"
          description="You’ve used your free tokens. Continue by exploring the Trading Live Chart."
          redirectUrl="/ask-chart-prop-funding"
          onClose={() => setShowAuthModal(false)}
        />
      )}
    </>
  );
};

export default TradingChatSessionPage;